import {
  useState,
  useEffect,
  useMemo,
  useRef,
  type CSSProperties,
  type HTMLAttributes,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import {
  closestCorners,
  DndContext,
  DragOverlay,
  getFirstCollision,
  KeyboardSensor,
  KeyboardCode,
  MouseSensor,
  TouchSensor,
  type Announcements,
  type DroppableContainer,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
  type KeyboardCoordinateGetter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Plus,
  ChevronDown,
  Check,
  Trash2,
  Pencil,
  MoreHorizontal,
  Circle,
  GripVertical,
} from "lucide-react";
import { fetchJsonCached, invalidateApiCache } from "../../../app/api/cache";
import {
  ConfirmModal,
  PromptModal,
} from "../../../components/ui/confirm-modal";

interface Board {
  id: string;
  title: string;
}
interface Column {
  id: string;
  boardId: string;
  title: string;
  order: number;
}
interface Card {
  id: string;
  columnId: string;
  title: string;
  description?: string;
  order: number;
  priority?: string;
  tags?: string[] | string;
  assignee?: string;
  assigneeName?: string;
}

const PRIORITY_CONFIG: Record<
  string,
  { color: string; bg: string; label: string }
> = {
  high: { color: "var(--color-error)", bg: "#fef2f2", label: "High" },
  medium: { color: "#d97706", bg: "#fffbeb", label: "Medium" },
  low: { color: "var(--color-secondary)", bg: "#f4f5f7", label: "Low" },
};

function formatPriority(p: string) {
  return PRIORITY_CONFIG[p] ?? PRIORITY_CONFIG.medium;
}

type CardLocation = {
  columnId: string;
  index: number;
  card: Card;
};

type CardMove = {
  nextCards: Record<string, Card[]>;
};

type ColumnMove = {
  nextColumns: Column[];
  changedColumns: Column[];
  movedColumn: Column;
};

const KEYBOARD_DIRECTIONS = [
  KeyboardCode.Down,
  KeyboardCode.Right,
  KeyboardCode.Up,
  KeyboardCode.Left,
];

const multipleContainersKeyboardCoordinates: KeyboardCoordinateGetter = (
  event,
  { context: { active, droppableRects, droppableContainers, collisionRect } },
) => {
  if (!KEYBOARD_DIRECTIONS.includes(event.code as KeyboardCode)) return;

  event.preventDefault();

  if (!active || !collisionRect) return;

  const filteredContainers: DroppableContainer[] = [];

  droppableContainers.getEnabled().forEach((entry) => {
    if (!entry || entry.disabled) return;

    const rect = droppableRects.get(entry.id);
    if (!rect) return;

    switch (event.code) {
      case KeyboardCode.Down:
        if (collisionRect.top < rect.top) filteredContainers.push(entry);
        break;
      case KeyboardCode.Up:
        if (collisionRect.top > rect.top) filteredContainers.push(entry);
        break;
      case KeyboardCode.Left:
        if (collisionRect.left >= rect.left + rect.width) {
          filteredContainers.push(entry);
        }
        break;
      case KeyboardCode.Right:
        if (collisionRect.left + collisionRect.width <= rect.left) {
          filteredContainers.push(entry);
        }
        break;
    }
  });

  const collisions = closestCorners({
    active,
    collisionRect,
    droppableRects,
    droppableContainers: filteredContainers,
    pointerCoordinates: null,
  });
  const closestId = getFirstCollision(collisions, "id");
  if (closestId == null) return;

  const newDroppable = droppableContainers.get(closestId);
  const newRect = newDroppable?.rect.current;
  if (!newDroppable?.node.current || !newRect) return;

  return {
    x: newRect.left,
    y: newRect.top,
  };
};

function findCardLocation(
  cardMap: Record<string, Card[]>,
  cardId: string,
): CardLocation | null {
  for (const [columnId, columnCards] of Object.entries(cardMap)) {
    const index = columnCards.findIndex((card) => card.id === cardId);
    if (index !== -1) return { columnId, index, card: columnCards[index] };
  }

  return null;
}

function getColumnTitle(columns: Column[], columnId: string) {
  return columns.find((column) => column.id === columnId)?.title ?? "column";
}

function getOverColumnId(
  cardMap: Record<string, Card[]>,
  columns: Column[],
  overId: string,
) {
  if (columns.some((column) => column.id === overId)) return overId;
  return findCardLocation(cardMap, overId)?.columnId ?? null;
}

function buildCardMove(
  cardMap: Record<string, Card[]>,
  columns: Column[],
  cardId: string,
  overId: string,
): CardMove | null {
  const source = findCardLocation(cardMap, cardId);
  if (!source) return null;

  const overColumn = columns.find((column) => column.id === overId);
  const overCard = overColumn ? null : findCardLocation(cardMap, overId);
  const targetColId = overColumn?.id ?? overCard?.columnId;
  if (!targetColId) return null;
  if (overColumn && source.columnId === targetColId) return null;

  const sourceCards = [...(cardMap[source.columnId] ?? [])];
  const targetCards =
    source.columnId === targetColId
      ? sourceCards
      : [...(cardMap[targetColId] ?? [])];
  const [cardToMove] = sourceCards.splice(source.index, 1);
  if (!cardToMove) return null;

  const requestedTargetIndex = overColumn
    ? targetCards.length
    : (overCard?.index ?? targetCards.length);
  const targetIndex =
    source.columnId === targetColId
      ? Math.min(requestedTargetIndex, targetCards.length)
      : requestedTargetIndex;

  if (source.columnId === targetColId && source.index === targetIndex) {
    return null;
  }

  targetCards.splice(targetIndex, 0, {
    ...cardToMove,
    columnId: targetColId,
  });

  const orderedSourceCards = sourceCards.map((card, index) => ({
    ...card,
    order: index,
  }));
  const orderedTargetCards = targetCards.map((card, index) => ({
    ...card,
    columnId: targetColId,
    order: index,
  }));

  const nextCards = {
    ...cardMap,
    [source.columnId]:
      source.columnId === targetColId ? orderedTargetCards : orderedSourceCards,
    [targetColId]: orderedTargetCards,
  };

  return {
    nextCards,
  };
}

function getChangedCards(
  previousCards: Record<string, Card[]>,
  nextCards: Record<string, Card[]>,
) {
  const previousById = new Map(
    Object.values(previousCards)
      .flat()
      .map((card) => [card.id, card]),
  );

  return Object.values(nextCards)
    .flat()
    .filter((card) => {
      const previous = previousById.get(card.id);
      return (
        !previous ||
        previous.columnId !== card.columnId ||
        previous.order !== card.order
      );
    });
}

function getCardTags(card: Card) {
  if (Array.isArray(card.tags)) return card.tags;
  if (!card.tags) return [];
  return card.tags
    .split(",")
    .map((tag) => tag.trim())
    .filter(Boolean);
}

function getAssigneeLabel(card: Card) {
  return card.assigneeName?.trim() || card.assignee?.trim() || "Unassigned";
}

function getAssigneeInitial(card: Card) {
  return getAssigneeLabel(card).charAt(0).toUpperCase();
}

function buildColumnMove(
  columns: Column[],
  activeId: string,
  overId: string,
): ColumnMove | null {
  const activeIndex = columns.findIndex((column) => column.id === activeId);
  const overIndex = columns.findIndex((column) => column.id === overId);
  if (activeIndex === -1 || overIndex === -1 || activeIndex === overIndex) {
    return null;
  }

  const movedColumn = columns[activeIndex];
  const nextColumns = arrayMove(columns, activeIndex, overIndex).map(
    (column, index) => ({
      ...column,
      order: index,
    }),
  );
  const previousOrderById = new Map(
    columns.map((column) => [column.id, column.order]),
  );

  return {
    nextColumns,
    changedColumns: nextColumns.filter(
      (column) => previousOrderById.get(column.id) !== column.order,
    ),
    movedColumn,
  };
}

function SortableKanbanColumn({
  column,
  children,
  disabled,
  className,
  childrenClassName,
}: {
  column: Column;
  children: (state: {
    columnDragHandleProps: HTMLAttributes<HTMLButtonElement>;
    isColumnDragging: boolean;
  }) => ReactNode;
  disabled: boolean;
  className: string;
  childrenClassName: string;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: column.id,
    data: { type: "column", column },
    disabled,
    attributes: {
      roleDescription: `Column: ${column.title}`,
    },
  });

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`${className} ${isDragging ? "opacity-40" : ""}`}
    >
      <div className={childrenClassName}>
        {children({
          columnDragHandleProps: disabled
            ? {}
            : { ...attributes, ...listeners },
          isColumnDragging: isDragging,
        })}
      </div>
    </div>
  );
}

function SortableKanbanCard({
  card,
  disabled,
  children,
}: {
  card: Card;
  disabled: boolean;
  children: (state: {
    dragHandleProps: HTMLAttributes<HTMLButtonElement>;
    isDragging: boolean;
  }) => ReactNode;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: card.id,
    data: { type: "card", card },
    disabled,
    attributes: {
      roleDescription: `Issue: ${card.title}`,
    },
  });

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style}>
      {children({
        dragHandleProps: disabled ? {} : { ...attributes, ...listeners },
        isDragging,
      })}
    </div>
  );
}

export function KanbanPanel() {
  const [boards, setBoards] = useState<Board[]>([]);
  const [activeBoardId, setActiveBoardId] = useState("");
  const [isBoardDropdownOpen, setIsBoardDropdownOpen] = useState(false);
  const [columns, setColumns] = useState<Column[]>([]);
  const [cards, setCards] = useState<Record<string, Card[]>>({});
  const [isLoaded, setIsLoaded] = useState(false);

  // Add/edit state
  const [isAddingTaskTo, setIsAddingTaskTo] = useState<string | null>(null);
  const [newTaskTitle, setNewTaskTitle] = useState("");
  const [isAddingColumn, setIsAddingColumn] = useState(false);
  const [newColumnTitle, setNewColumnTitle] = useState("");
  const [editingCardId, setEditingCardId] = useState<string | null>(null);
  const [editCardTitle, setEditCardTitle] = useState("");
  const [editCardPriority, setEditCardPriority] = useState("medium");
  const [editCardTags, setEditCardTags] = useState("");

  // Rename state
  const [renamingBoardInDropdown, setRenamingBoardInDropdown] = useState(false);
  const [renameBoardValue, setRenameBoardValue] = useState("");
  const [renamingColId, setRenamingColId] = useState<string | null>(null);
  const [renameColValue, setRenameColValue] = useState("");

  // All-projects view
  const [allColumns, setAllColumns] = useState<Column[]>([]);
  const [allCards, setAllCards] = useState<Record<string, Card[]>>({});

  // Drag state
  const [activeCard, setActiveCard] = useState<Card | null>(null);
  const [activeColumn, setActiveColumn] = useState<Column | null>(null);
  const dragStartCardsRef = useRef<Record<string, Card[]> | null>(null);
  const dragCardsRef = useRef<Record<string, Card[]> | null>(null);

  // Modals
  const [confirmState, setConfirmState] = useState<{
    message: string;
    onConfirm: () => void;
    label?: string;
    variant?: "danger" | "primary";
  } | null>(null);
  const [promptState, setPromptState] = useState<{
    title: string;
    placeholder?: string;
    onSubmit: (v: string) => void;
  } | null>(null);

  const confirm = (
    message: string,
    onConfirm: () => void,
    label?: string,
    variant?: "danger" | "primary",
  ) => setConfirmState({ message, onConfirm, label, variant });
  const prompt = (
    title: string,
    onSubmit: (v: string) => void,
    placeholder?: string,
  ) => setPromptState({ title, placeholder, onSubmit });

  const activeBoard = boards.find((b) => b.id === activeBoardId);
  const isAllView = activeBoardId === "all";
  const displayColumns = isAllView ? allColumns : columns;
  const displayCards = isAllView ? allCards : cards;
  const activeColumnCards = activeColumn ? (cards[activeColumn.id] ?? []) : [];
  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(TouchSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: multipleContainersKeyboardCoordinates,
    }),
  );

  const announcements: Announcements = useMemo(
    () => ({
      onDragStart({ active }) {
        const activeData = active.data.current;
        if (activeData?.type === "column") {
          const startColumnIdx = columns.findIndex(
            (column) => column.id === active.id,
          );
          const startColumn = columns[startColumnIdx];
          if (!startColumn) return;
          return `Picked up column ${startColumn.title} at position ${
            startColumnIdx + 1
          } of ${columns.length}.`;
        }

        const location = findCardLocation(cards, String(active.id));
        if (!location) return;
        return `Picked up issue ${location.card.title} at position ${
          location.index + 1
        } of ${(cards[location.columnId] ?? []).length} in ${getColumnTitle(
          columns,
          location.columnId,
        )}.`;
      },
      onDragOver({ active, over }) {
        if (!over) return;
        const activeData = active.data.current;
        if (activeData?.type === "column") {
          const overColumnIdx = columns.findIndex(
            (column) => column.id === over.id,
          );
          const overColumn = columns[overColumnIdx];
          if (!overColumn) return;
          return `Column ${activeData.column.title} moved over ${
            overColumn.title
          } at position ${overColumnIdx + 1} of ${columns.length}.`;
        }

        const activeLocation = findCardLocation(cards, String(active.id));
        const overColumnId = getOverColumnId(cards, columns, String(over.id));
        if (!activeLocation || !overColumnId) return;

        if (activeLocation.columnId !== overColumnId) {
          return `Issue ${activeLocation.card.title} moved over ${getColumnTitle(
            columns,
            overColumnId,
          )}.`;
        }

        const overLocation = findCardLocation(cards, String(over.id));
        if (!overLocation) return;
        return `Issue ${activeLocation.card.title} moved to position ${
          overLocation.index + 1
        } in ${getColumnTitle(columns, overColumnId)}.`;
      },
      onDragEnd({ active, over }) {
        const activeData = active.data.current;
        if (activeData?.type === "column") {
          if (!over) return `Moving column ${activeData.column.title} cancelled.`;
          const overColumnIdx = columns.findIndex(
            (column) => column.id === over.id,
          );
          if (overColumnIdx === -1) return;
          return `Column ${
            activeData.column.title
          } dropped at position ${overColumnIdx + 1} of ${columns.length}.`;
        }

        const activeLocation = findCardLocation(cards, String(active.id));
        if (!activeLocation) return;
        if (!over) return `Moving issue ${activeLocation.card.title} cancelled.`;

        const overColumnId = getOverColumnId(cards, columns, String(over.id));
        if (!overColumnId) return;

        return `Issue ${activeLocation.card.title} dropped in ${getColumnTitle(
          columns,
          overColumnId,
        )}.`;
      },
      onDragCancel({ active }) {
        const activeData = active.data.current;
        if (activeData?.type === "column") {
          return `Moving column ${activeData.column.title} cancelled.`;
        }

        const location = findCardLocation(cards, String(active.id));
        if (!location) return;
        return `Moving issue ${location.card.title} cancelled.`;
      },
    }),
    [cards, columns],
  );

  // ---- Data loading ----
  async function loadBoards() {
    try {
      const data = await fetchJsonCached<Board[]>("/api/kanban/boards");
      setBoards(data);
      if (data.length > 0) setActiveBoardId((prev) => prev || data[0].id);
    } catch {}
    setIsLoaded(true);
  }

  async function loadBoard(boardId: string) {
    try {
      const cols = await fetchJsonCached<Column[]>(
        `/api/kanban/boards/${boardId}/columns`,
      );
      setColumns(cols);
      const cardMap: Record<string, Card[]> = {};
      await Promise.all(
        cols.map(async (col) => {
          cardMap[col.id] = await fetchJsonCached<Card[]>(
            `/api/kanban/columns/${col.id}/cards`,
          );
        }),
      );
      setCards(cardMap);
    } catch {}
  }

  async function loadAllBoards() {
    const mergedMap = new Map<string, Column>();
    const mergedCards: Record<string, Card[]> = {};
    for (const board of boards) {
      try {
        const cols = await fetchJsonCached<Column[]>(
          `/api/kanban/boards/${board.id}/columns`,
        );
        for (const col of cols) {
          const key = col.title.toLowerCase().trim();
          if (!mergedMap.has(key)) {
            mergedMap.set(key, { ...col, id: `all-${key}` });
            mergedCards[`all-${key}`] = [];
          }
          const colCards = await fetchJsonCached<Card[]>(
            `/api/kanban/columns/${col.id}/cards`,
          );
          mergedCards[`all-${key}`] = [
            ...mergedCards[`all-${key}`],
            ...colCards.map((c) => ({
              ...c,
              title: `[${board.title}] ${c.title}`,
            })),
          ];
        }
      } catch {}
    }
    setAllColumns(Array.from(mergedMap.values()));
    setAllCards(mergedCards);
  }

  useEffect(() => {
    loadBoards();
  }, []);
  useEffect(() => {
    if (!activeBoardId || activeBoardId === "all") return;
    loadBoard(activeBoardId);
  }, [activeBoardId]);
  useEffect(() => {
    if (activeBoardId === "all" && boards.length > 0) loadAllBoards();
  }, [activeBoardId, boards]);

  // ---- Mutations ----
  async function createBoard(title: string) {
    try {
      const res = await fetch("/api/kanban/boards", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title }),
      });
      if (res.ok) {
        invalidateApiCache("/api/kanban");
        const board: Board = await res.json();
        setBoards((prev) => [...prev, board]);
        setActiveBoardId(board.id);
      }
    } catch {}
    setIsBoardDropdownOpen(false);
  }

  async function renameBoard() {
    if (!renameBoardValue.trim() || !activeBoardId) return;
    setRenamingBoardInDropdown(false);
    try {
      const res = await fetch(`/api/kanban/boards/${activeBoardId}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title: renameBoardValue.trim() }),
      });
      if (res.ok) {
        invalidateApiCache("/api/kanban");
        const updated: Board = await res.json();
        setBoards((prev) =>
          prev.map((b) => (b.id === updated.id ? updated : b)),
        );
      }
    } catch {}
  }

  async function renameColumn(colId: string) {
    const title = renameColValue.trim();
    setRenamingColId(null);
    if (!title) return;
    try {
      const res = await fetch(`/api/kanban/columns/${colId}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title }),
      });
      if (res.ok) {
        invalidateApiCache("/api/kanban");
        const updated: Column = await res.json();
        setColumns((prev) =>
          prev.map((c) => (c.id === updated.id ? updated : c)),
        );
      }
    } catch {}
  }

  async function doDeleteBoard() {
    try {
      await fetch(`/api/kanban/boards/${activeBoardId}`, {
        method: "DELETE",
        credentials: "include",
      });
      invalidateApiCache("/api/kanban");
      const remaining = boards.filter((b) => b.id !== activeBoardId);
      setBoards(remaining);
      setActiveBoardId(remaining[0]?.id ?? "");
      setColumns([]);
      setCards({});
    } catch {}
    setIsBoardDropdownOpen(false);
  }

  async function submitNewColumn() {
    const title = newColumnTitle.trim();
    setIsAddingColumn(false);
    setNewColumnTitle("");
    if (!title) return;
    try {
      const res = await fetch(`/api/kanban/boards/${activeBoardId}/columns`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title }),
      });
      if (res.ok) {
        invalidateApiCache("/api/kanban");
        const col: Column = await res.json();
        setColumns((prev) => [...prev, col]);
        setCards((prev) => ({ ...prev, [col.id]: [] }));
      }
    } catch {}
  }

  async function doDeleteColumn(colId: string) {
    try {
      await fetch(`/api/kanban/columns/${colId}`, {
        method: "DELETE",
        credentials: "include",
      });
      invalidateApiCache("/api/kanban");
      setColumns((prev) => prev.filter((c) => c.id !== colId));
      setCards((prev) => {
        const next = { ...prev };
        delete next[colId];
        return next;
      });
    } catch {}
  }

  async function submitNewTask(colId: string) {
    const title = newTaskTitle.trim();
    setIsAddingTaskTo(null);
    setNewTaskTitle("");
    if (!title) return;
    try {
      const res = await fetch(`/api/kanban/columns/${colId}/cards`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title }),
      });
      if (res.ok) {
        invalidateApiCache("/api/kanban");
        const card: Card = await res.json();
        setCards((prev) => ({
          ...prev,
          [colId]: [...(prev[colId] ?? []), card],
        }));
      }
    } catch {}
  }

  async function saveEditTask(cardId: string, colId: string) {
    const title = editCardTitle.trim();
    setEditingCardId(null);
    if (!title) return;
    const tags = editCardTags
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    try {
      const res = await fetch(`/api/kanban/cards/${cardId}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title, priority: editCardPriority, tags }),
      });
      if (res.ok) {
        invalidateApiCache("/api/kanban");
        const updated: Card = await res.json();
        setCards((prev) => ({
          ...prev,
          [colId]: (prev[colId] ?? []).map((c) =>
            c.id === cardId ? { ...c, ...updated } : c,
          ),
        }));
      }
    } catch {}
  }

  async function doDeleteCard(cardId: string, colId: string) {
    try {
      await fetch(`/api/kanban/cards/${cardId}`, {
        method: "DELETE",
        credentials: "include",
      });
      invalidateApiCache("/api/kanban");
      setCards((prev) => ({
        ...prev,
        [colId]: (prev[colId] ?? []).filter((c) => c.id !== cardId),
      }));
    } catch {}
    setEditingCardId(null);
  }

  // ---- Drag & Drop ----
  function handleDragStart(event: DragStartEvent) {
    if (isAllView) return;
    const activeData = event.active.data.current;
    if (activeData?.type === "column") {
      setActiveColumn(activeData.column as Column);
      return;
    }

    const location = findCardLocation(cards, String(event.active.id));
    dragStartCardsRef.current = cards;
    dragCardsRef.current = cards;
    setActiveCard(location?.card ?? null);
  }

  function handleDragOver(event: DragOverEvent) {
    if (event.active.data.current?.type === "column") return;

    if (isAllView || !event.over) {
      return;
    }

    const currentCards = dragCardsRef.current ?? cards;
    const move = buildCardMove(
      currentCards,
      columns,
      String(event.active.id),
      String(event.over.id),
    );

    if (move) {
      dragCardsRef.current = move.nextCards;
      setCards(move.nextCards);
    }
  }

  async function handleDragEnd(event: DragEndEvent) {
    setActiveCard(null);
    setActiveColumn(null);

    if (event.active.data.current?.type === "column") {
      if (!event.over || isAllView) return;

      const move = buildColumnMove(
        columns,
        String(event.active.id),
        String(event.over.id),
      );
      if (!move) return;

      setColumns(move.nextColumns);

      try {
        await Promise.all(
          move.changedColumns.map((column) =>
            fetch(`/api/kanban/columns/${column.id}`, {
              method: "PATCH",
              credentials: "include",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ order: column.order }),
            }).then((res) => {
              if (!res.ok) throw new Error("Column move failed");
            }),
          ),
        );
        invalidateApiCache("/api/kanban");
      } catch {
        invalidateApiCache("/api/kanban");
        await loadBoard(activeBoardId);
      }
      return;
    }

    const startCards = dragStartCardsRef.current;
    let finalCards = dragCardsRef.current ?? cards;

    dragStartCardsRef.current = null;
    dragCardsRef.current = null;

    if (!event.over || isAllView) {
      if (startCards) setCards(startCards);
      return;
    }

    if (startCards && finalCards === startCards) {
      const move = buildCardMove(
        startCards,
        columns,
        String(event.active.id),
        String(event.over.id),
      );
      if (move) {
        finalCards = move.nextCards;
        setCards(finalCards);
      }
    }

    const changedCards = startCards
      ? getChangedCards(startCards, finalCards)
      : Object.values(finalCards).flat();
    const movedCard =
      findCardLocation(finalCards, String(event.active.id))?.card ?? activeCard;

    if (changedCards.length === 0 || !movedCard) return;

    try {
      await Promise.all(
        changedCards.map((card) =>
          fetch(`/api/kanban/cards/${card.id}`, {
            method: "PATCH",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ columnId: card.columnId, order: card.order }),
          }).then((res) => {
            if (!res.ok) throw new Error("Move failed");
          }),
        ),
      );
      invalidateApiCache("/api/kanban");
    } catch {
      invalidateApiCache("/api/kanban");
      await loadBoard(activeBoardId);
    }
  }

  function handleDragCancel() {
    if (dragStartCardsRef.current) setCards(dragStartCardsRef.current);
    dragStartCardsRef.current = null;
    dragCardsRef.current = null;
    setActiveCard(null);
    setActiveColumn(null);
  }

  // ---- Render helpers ----
  function renderPriorityBadge(priority?: string) {
    if (!priority) return null;
    const cfg = formatPriority(priority);
    return (
      <span
        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium"
        style={{ backgroundColor: cfg.bg, color: cfg.color }}
      >
        <Circle size={8} fill={cfg.color} stroke="none" />
        {cfg.label}
      </span>
    );
  }

  function renderTags(tags?: string[]) {
    if (!tags || tags.length === 0) return null;
    return (
      <>
        {tags.map((tag) => (
          <span
            key={tag}
            className="text-xs px-1.5 py-0.5 rounded bg-surface-container-high text-secondary font-medium"
          >
            {tag}
          </span>
        ))}
      </>
    );
  }

  function renderAssignee(card: Card) {
    const label = getAssigneeLabel(card);
    return (
      <div className="flex items-center gap-1.5 min-w-0 text-outline">
        <span className="inline-flex size-5 shrink-0 items-center justify-center rounded-full bg-surface-container-high text-[10px] font-semibold text-secondary">
          {getAssigneeInitial(card)}
        </span>
        <span className="truncate text-xs font-medium">{label}</span>
      </div>
    );
  }

  // ---- Empty state ----
  if (isLoaded && boards.length === 0) {
    return (
      <div className="h-full flex flex-col p-md sm:p-margin-page overflow-x-hidden">
        <h1 className="font-h1 text-primary mb-xl">Projects</h1>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <p className="font-body-md text-secondary mb-md">No boards yet.</p>
            <button
              onClick={() =>
                prompt("Create New Board", createBoard, "Board name…")
              }
              className="bg-primary text-on-primary font-label-sm px-4 py-2 rounded flex items-center gap-2 hover:opacity-90 transition-opacity mx-auto"
            >
              <Plus size={18} /> Create Board
            </button>
          </div>
        </div>
        {promptState && (
          <PromptModal
            title={promptState.title}
            placeholder={promptState.placeholder}
            onSubmit={promptState.onSubmit}
            onCancel={() => setPromptState(null)}
          />
        )}
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col p-md sm:p-margin-page overflow-auto overflow-x-hidden md:overflow-hidden">
      {/* Modals */}
      {confirmState && (
        <ConfirmModal
          message={confirmState.message}
          onConfirm={() => {
            confirmState.onConfirm();
            setConfirmState(null);
          }}
          onCancel={() => setConfirmState(null)}
          confirmLabel={confirmState.label}
          variant={confirmState.variant}
        />
      )}
      {promptState && (
        <PromptModal
          title={promptState.title}
          placeholder={promptState.placeholder}
          onSubmit={(v) => {
            promptState.onSubmit(v);
            setPromptState(null);
          }}
          onCancel={() => setPromptState(null)}
        />
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-md mb-lg sm:mb-xl shrink-0">
        <div>
          <div className="relative">
            <button
              onClick={() => {
                setIsBoardDropdownOpen(!isBoardDropdownOpen);
                setRenamingBoardInDropdown(false);
              }}
              className="font-h2 sm:font-h1 text-primary flex items-center gap-2 text-left hover:opacity-80 transition-opacity"
            >
              {isAllView
                ? "All Projects Issues"
                : (activeBoard?.title ?? "Projects")}
              <ChevronDown size={24} />
            </button>

            {isBoardDropdownOpen && (
              <div className="absolute top-full left-0 mt-2 w-72 bg-surface-container-lowest border border-outline-variant shadow-lg rounded-lg overflow-hidden z-20">
                {boards.map((b) => (
                  <button
                    key={b.id}
                    onClick={() => {
                      setActiveBoardId(b.id);
                      setIsBoardDropdownOpen(false);
                      setRenamingBoardInDropdown(false);
                    }}
                    className="w-full text-left px-4 py-3 hover:bg-surface-variant transition-colors flex items-center justify-between font-body-md text-primary"
                  >
                    {b.title}
                    {b.id === activeBoardId && (
                      <Check size={16} className="text-primary" />
                    )}
                  </button>
                ))}
                <button
                  onClick={() => {
                    setActiveBoardId("all");
                    setIsBoardDropdownOpen(false);
                  }}
                  className="w-full text-left px-4 py-3 hover:bg-surface-variant transition-colors flex items-center justify-between font-body-md text-secondary font-medium"
                >
                  All Projects Issues
                  {activeBoardId === "all" && (
                    <Check size={16} className="text-primary" />
                  )}
                </button>
                <div className="border-t border-outline-variant" />
                {renamingBoardInDropdown ? (
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      renameBoard();
                    }}
                    className="px-4 py-2 flex items-center gap-2"
                  >
                    <input
                      autoFocus
                      type="text"
                      value={renameBoardValue}
                      onChange={(e) => setRenameBoardValue(e.target.value)}
                      onBlur={() => renameBoard()}
                      className="flex-1 bg-surface-container-low border border-primary rounded px-2 py-1 font-body-md text-primary outline-none"
                    />
                    <button
                      type="submit"
                      className="text-primary font-label-sm"
                    >
                      Save
                    </button>
                  </form>
                ) : (
                  <button
                    onClick={() => {
                      setRenamingBoardInDropdown(true);
                      setRenameBoardValue(activeBoard?.title ?? "");
                    }}
                    className="w-full text-left px-4 py-3 hover:bg-surface-variant transition-colors text-secondary font-label-sm flex items-center gap-2"
                  >
                    <Pencil size={16} /> Rename Board
                  </button>
                )}
                <button
                  onClick={() => {
                    if (boards.length <= 1) {
                      confirm(
                        "Cannot delete the last board. Create another board first.",
                        () => setConfirmState(null),
                        "OK",
                        "primary",
                      );
                    } else {
                      confirm(
                        `Delete board "${activeBoard?.title}"? This will delete all columns and cards.`,
                        doDeleteBoard,
                      );
                    }
                    setIsBoardDropdownOpen(false);
                  }}
                  className="w-full text-left px-4 py-3 hover:bg-surface-variant transition-colors text-error font-label-sm flex items-center gap-2"
                >
                  <Trash2 size={16} /> Delete Board
                </button>
                <button
                  onClick={() => {
                    setIsBoardDropdownOpen(false);
                    prompt("Create New Board", createBoard, "Board name…");
                  }}
                  className="w-full text-left px-4 py-3 hover:bg-surface-variant transition-colors text-secondary font-label-sm flex items-center gap-2"
                >
                  <Plus size={16} /> Create New Board
                </button>
              </div>
            )}
          </div>
          <p className="font-body-md text-secondary mt-1">
            {isAllView
              ? "Aggregated view across all project boards."
              : "Track sprint progress and backlog items."}
          </p>
        </div>
        {!isAllView && (
          <div className="flex items-center gap-sm">
            <button
              onClick={() => {
                if (displayColumns.length > 0) {
                  setIsAddingTaskTo(displayColumns[0].id);
                  setNewTaskTitle("");
                }
              }}
              className="bg-primary text-on-primary font-label-sm px-4 py-2 rounded flex items-center gap-2 hover:opacity-90 transition-opacity shadow-sm cursor-pointer"
            >
              <Plus size={18} /> New Issue
            </button>
          </div>
        )}
      </div>

      {/* Columns */}
      <DndContext
        accessibility={{ announcements }}
        collisionDetection={closestCorners}
        sensors={sensors}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <div className="flex flex-col md:flex-row md:flex-1 gap-md md:gap-lg md:overflow-x-auto pb-4 scrollbar-hide items-stretch md:items-start">
          <SortableContext
            items={displayColumns.map((column) => column.id)}
            strategy={rectSortingStrategy}
          >
            {displayColumns.map((col) => {
              const colCards = displayCards[col.id] ?? [];
              return (
                <SortableKanbanColumn
                  key={col.id}
                  column={col}
                  disabled={isAllView || renamingColId === col.id}
                  className="w-full md:w-[340px] md:shrink-0 snap-start"
                  childrenClassName="flex flex-col md:max-h-full rounded-lg transition-colors duration-150"
                >
                  {({ columnDragHandleProps }) => (
                    <>
                {/* Column Header */}
                <div className="flex justify-between items-center mb-md shrink-0 group/col-header px-1">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    {renamingColId === col.id ? (
                      <form
                        onSubmit={(e) => {
                          e.preventDefault();
                          renameColumn(col.id);
                        }}
                        className="flex-1"
                      >
                        <input
                          autoFocus
                          type="text"
                          value={renameColValue}
                          onChange={(e) => setRenameColValue(e.target.value)}
                          onBlur={() => renameColumn(col.id)}
                          className="font-h3 text-primary bg-transparent border-b-2 border-primary outline-none w-full"
                      />
                    </form>
                  ) : (
                    <>
                      {!isAllView && (
                        <button
                          type="button"
                          {...columnDragHandleProps}
                          className="text-outline hover:text-primary transition-colors md:opacity-0 md:group-hover/col-header:opacity-100 cursor-grab active:cursor-grabbing focus:opacity-100 focus:text-primary focus:outline-none focus:ring-2 focus:ring-primary/30 rounded p-1"
                          title="Move section"
                        >
                          <span className="sr-only">
                            Move section {col.title}
                          </span>
                          <GripVertical size={16} />
                        </button>
                      )}
                      <h3 className="font-h3 text-primary truncate">
                        {col.title}
                      </h3>
                      <span className="font-mono-sm text-secondary bg-surface-container-low px-2 py-0.5 rounded-full shrink-0">
                        {colCards.length}
                      </span>
                      </>
                    )}
                  </div>
                  {!isAllView && renamingColId !== col.id && (
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        type="button"
                        onClick={() => {
                          setRenamingColId(col.id);
                          setRenameColValue(col.title);
                        }}
                        className="text-secondary hover:text-primary transition-colors md:opacity-0 md:group-hover/col-header:opacity-100 p-1"
                        title="Rename"
                      >
                        <Pencil size={16} />
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          confirm("Delete this section and all its tasks?", () =>
                            doDeleteColumn(col.id),
                          )
                        }
                        className="text-secondary hover:text-error transition-colors md:opacity-0 md:group-hover/col-header:opacity-100 p-1"
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setIsAddingTaskTo(col.id);
                          setNewTaskTitle("");
                        }}
                        className="text-secondary hover:text-primary transition-colors p-1"
                        title="Add issue"
                      >
                        <Plus size={20} />
                      </button>
                    </div>
                  )}
                </div>

                {/* Cards */}
                <div className="md:overflow-y-auto md:flex-1 pr-0 md:pr-1 scrollbar-hide rounded-lg transition-colors min-h-[80px]">
                  <SortableContext
                    items={colCards.map((card) => card.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    {colCards.map((card) => (
                      <SortableKanbanCard
                        key={card.id}
                        card={card}
                        disabled={isAllView || editingCardId === card.id}
                      >
                        {({ dragHandleProps, isDragging: isCardDragging }) =>
                          !isAllView && editingCardId === card.id ? (
                            <div className="bg-surface-container-lowest border-2 border-primary rounded-lg p-md shadow-sm mb-md flex flex-col gap-3">
                              <input
                                autoFocus
                                type="text"
                                value={editCardTitle}
                                onChange={(e) =>
                                  setEditCardTitle(e.target.value)
                                }
                                onKeyDown={(e) => {
                                  if (e.key === "Enter")
                                    saveEditTask(card.id, col.id);
                                  if (e.key === "Escape")
                                    setEditingCardId(null);
                                }}
                                className="bg-surface-container-low border border-outline-variant rounded px-2 py-1 outline-none text-primary font-body-md w-full focus:border-primary"
                                placeholder="Task Title"
                              />
                              <div className="flex flex-col gap-1 sm:flex-row sm:gap-2 sm:items-center">
                                <span className="font-label-sm text-secondary">
                                  Priority:
                                </span>
                                <select
                                  value={editCardPriority}
                                  onChange={(e) =>
                                    setEditCardPriority(e.target.value)
                                  }
                                  className="bg-surface-container-low border border-outline-variant rounded px-2 py-1 text-xs outline-none"
                                >
                                  <option value="high">🔴 High</option>
                                  <option value="medium">🟠 Medium</option>
                                  <option value="low">⚪ Low</option>
                                </select>
                              </div>
                              <div className="flex flex-col gap-1 sm:block">
                                <span className="font-label-sm text-secondary">
                                  Tags:
                                </span>
                                <input
                                  type="text"
                                  value={editCardTags}
                                  onChange={(e) =>
                                    setEditCardTags(e.target.value)
                                  }
                                  placeholder="design, frontend, bug"
                                  className="bg-surface-container-low border border-outline-variant rounded px-2 py-1 text-xs outline-none w-full sm:ml-2 sm:w-40 focus:border-primary"
                                />
                              </div>
                              <div className="flex justify-end gap-2">
                                <button
                                  type="button"
                                  onClick={() => setEditingCardId(null)}
                                  className="text-secondary font-label-sm hover:text-primary transition-colors"
                                >
                                  Cancel
                                </button>
                                <button
                                  type="button"
                                  onClick={() =>
                                    confirm("Delete task?", () =>
                                      doDeleteCard(card.id, col.id),
                                    )
                                  }
                                  className="text-error font-label-sm hover:opacity-80 transition-colors"
                                >
                                  Delete
                                </button>
                                <button
                                  type="button"
                                  onClick={() => saveEditTask(card.id, col.id)}
                                  className="bg-primary text-on-primary px-3 py-1 rounded font-label-sm hover:opacity-90 transition-opacity"
                                >
                                  Save
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div
                              className={`bg-surface-container-lowest border border-outline-variant rounded-lg p-md shadow-sm hover:shadow-md hover:border-primary/30 transition-colors group mb-md ${
                                isCardDragging
                                  ? "opacity-40 ring-2 ring-primary/30"
                                  : ""
                              }`}
                            >
                              <div className="flex justify-between items-start gap-2 mb-xs">
                                <div className="min-w-0 flex-1">
                                  {renderPriorityBadge(card.priority)}
                                </div>
                                {!isAllView && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setEditingCardId(card.id);
                                      setEditCardTitle(card.title);
                                      setEditCardPriority(
                                        card.priority || "medium",
                                      );
                                      setEditCardTags(
                                        getCardTags(card).join(", "),
                                      );
                                    }}
                                    className="text-outline hover:text-primary transition-colors md:opacity-0 md:group-hover:opacity-100 p-1"
                                    title="Edit issue"
                                  >
                                    <MoreHorizontal size={16} />
                                  </button>
                                )}
                              </div>
                              <div className="flex items-start gap-2">
                                {!isAllView && (
                                  <button
                                    type="button"
                                    {...dragHandleProps}
                                    className="mt-0.5 inline-flex shrink-0 text-outline opacity-70 md:opacity-0 md:group-hover:opacity-60 cursor-grab active:cursor-grabbing focus:opacity-100 focus:text-primary focus:outline-none focus:ring-2 focus:ring-primary/30 rounded"
                                    title="Move issue"
                                  >
                                    <span className="sr-only">
                                      Move issue {card.title}
                                    </span>
                                    <GripVertical size={14} />
                                  </button>
                                )}
                                <div className="min-w-0 flex-1">
                                  <h4 className="font-body-md text-primary leading-snug">
                                    {card.title}
                                  </h4>
                                  {card.description && (
                                    <p className="font-label-sm text-outline mt-1 line-clamp-2">
                                      {card.description}
                                    </p>
                                  )}
                                  <div className="mt-sm flex items-end justify-between gap-2">
                                    <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                                      {renderTags(getCardTags(card))}
                                    </div>
                                    <div className="max-w-[45%] shrink-0">
                                      {renderAssignee(card)}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )
                        }
                      </SortableKanbanCard>
                    ))}
                  </SortableContext>

                  {!isAllView && isAddingTaskTo === col.id && (
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        submitNewTask(col.id);
                      }}
                      className="bg-surface-container-lowest border border-primary rounded-lg p-md shadow-sm mb-md"
                    >
                      <input
                        autoFocus
                        type="text"
                        value={newTaskTitle}
                        onChange={(e) => setNewTaskTitle(e.target.value)}
                        onBlur={() => submitNewTask(col.id)}
                        className="w-full bg-transparent outline-none font-body-md text-primary placeholder:text-outline"
                        placeholder="What needs to be done?"
                      />
                    </form>
                  )}
                </div>
                    </>
                  )}
                </SortableKanbanColumn>
              );
            })}
          </SortableContext>

          {/* Add Section */}
          {!isAllView &&
            (isAddingColumn ? (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  submitNewColumn();
                }}
                className="w-full md:w-[340px] md:shrink-0 p-md border-2 border-primary border-dashed rounded-lg md:mt-[38px] bg-surface-container-lowest h-min min-h-[88px] md:min-h-[100px] flex items-center"
              >
                <input
                  autoFocus
                  type="text"
                  value={newColumnTitle}
                  onChange={(e) => setNewColumnTitle(e.target.value)}
                  onBlur={() => submitNewColumn()}
                  className="w-full bg-transparent outline-none font-body-md text-primary text-center"
                  placeholder="Section Name"
                />
              </form>
            ) : (
              <button
                type="button"
                onClick={() => {
                  setIsAddingColumn(true);
                  setNewColumnTitle("");
                }}
                className="w-full md:w-[340px] md:shrink-0 flex items-center justify-center p-md border-2 border-dashed border-outline-variant rounded-lg md:mt-[38px] hover:border-outline hover:bg-surface-container-lowest transition-colors cursor-pointer text-secondary hover:text-primary h-min min-h-[88px] md:min-h-[100px]"
              >
                <span className="font-label-sm flex items-center gap-2">
                  <Plus size={18} /> Add Section
                </span>
              </button>
            ))}
        </div>

        {typeof document !== "undefined" &&
          createPortal(
            <DragOverlay>
              {activeColumn && (
                <div className="w-[340px] max-w-[calc(100vw-2rem)] rounded-lg bg-surface-container-low p-sm shadow-lg ring-2 ring-primary/30">
                  <div className="flex items-center gap-2 px-1 pb-md">
                    <GripVertical size={16} className="text-outline" />
                    <h3 className="font-h3 text-primary truncate">
                      {activeColumn.title}
                    </h3>
                    <span className="font-mono-sm text-secondary bg-surface-container-lowest px-2 py-0.5 rounded-full shrink-0">
                      {activeColumnCards.length}
                    </span>
                  </div>
                  <div className="max-h-[360px] overflow-hidden">
                    {activeColumnCards.slice(0, 4).map((card) => (
                      <div
                        key={card.id}
                        className="bg-surface-container-lowest border border-outline-variant rounded-lg p-md shadow-sm mb-md"
                      >
                        {renderPriorityBadge(card.priority)}
                        <h4 className="font-body-md text-primary leading-snug mt-xs">
                          {card.title}
                        </h4>
                        <div className="mt-sm flex items-end justify-between gap-2">
                          <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                            {renderTags(getCardTags(card))}
                          </div>
                          <div className="max-w-[45%] shrink-0">
                            {renderAssignee(card)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {activeCard && (
                <div className="w-[340px] max-w-[calc(100vw-2rem)] bg-surface-container-lowest border border-primary/30 rounded-lg p-md shadow-lg ring-2 ring-primary/30">
                  <div className="mb-xs">{renderPriorityBadge(activeCard.priority)}</div>
                  <div className="flex items-start gap-2">
                    <span className="mt-0.5 inline-flex shrink-0 text-outline opacity-70">
                      <GripVertical size={14} />
                    </span>
                    <div className="min-w-0 flex-1">
                      <h4 className="font-body-md text-primary leading-snug">
                        {activeCard.title}
                      </h4>
                      {activeCard.description && (
                        <p className="font-label-sm text-outline mt-1 line-clamp-2">
                          {activeCard.description}
                        </p>
                      )}
                      <div className="mt-sm flex items-end justify-between gap-2">
                        <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                          {renderTags(getCardTags(activeCard))}
                        </div>
                        <div className="max-w-[45%] shrink-0">
                          {renderAssignee(activeCard)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </DragOverlay>,
            document.body,
          )}
      </DndContext>
    </div>
  );
}
